import React from 'react';
import { ArrowUp, Loader2 } from 'lucide-react';
import { useSubmit, useTextareaAutoSize } from '@norkin/chat-react';
import type { Id } from '@norkin/chat-core';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export type ChatComposerProps = {
  threadId: Id;
  disabled?: boolean;
  onSend?: (assistantMessageId: Id) => Promise<void> | void;
  className?: string;
};

export function ChatComposer({ threadId, disabled, onSend, className }: ChatComposerProps) {
  const [value, setValue] = React.useState('');
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const { submitUserText } = useSubmit(threadId);
  const {
    ref: autoSizeRef,
    onInput: handleAutoSizeInput,
    onFocus: handleAutoSizeFocus,
  } = useTextareaAutoSize(value);
  const isEmpty = value.trim().length === 0;

  const sendMessage = React.useCallback(async () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    const { assistantMessageId } = submitUserText(trimmed);
    setValue('');
    textareaRef.current?.focus();
    autoSizeRef(textareaRef.current);
    if (!assistantMessageId) return;
    try {
      await onSend?.(assistantMessageId);
    } catch (error) {
      console.error('[shadcn-chat] Failed to send message', error);
    }
  }, [autoSizeRef, value, disabled, submitUserText, onSend]);

  return (
    <form
      className={cn('rounded-lg border border-border bg-card/60 p-4 shadow-sm', className)}
      onSubmit={event => {
        event.preventDefault();
        void sendMessage();
      }}
    >
      <div className="relative">
        <Textarea
          aria-label="Message"
          value={value}
          onChange={event => setValue(event.target.value)}
          placeholder="Ask anything, shift + enter for newline"
          disabled={disabled}
          rows={1}
          ref={node => {
            textareaRef.current = node;
            autoSizeRef(node);
          }}
          onInput={handleAutoSizeInput}
          onFocus={handleAutoSizeFocus}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void sendMessage();
            }
          }}
          className="pr-16"
        />
        <Button
          type="submit"
          aria-label="Send message"
          disabled={disabled || isEmpty}
          className="absolute bottom-2 right-2 h-9 w-9 rounded-full p-0"
        >
          {disabled ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
        </Button>
      </div>
    </form>
  );
}
